import { useMutation } from '@tanstack/react-query';
import { isNil, some } from 'lodash';

import {
    UpdateSubscriptionTagsConfigurationsParams,
    UpdatedSubscriptionTagsConfigurationsResponse,
} from '@/api/subscriptionTags/types';
import { UpdateSubscriptionItem } from '@/api/subscriptions';
import { axios } from '@/common/axios';
import { MutationConfig, queryClient } from '@/common/react-query';
import { STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY, SUBSCRIPTION_TAGS_INFO_QUERY_KEY } from '@/config';

/**
 * This is much longer than we actually expect it to take, but we have other mechanisms to show transitioning states before
 * wavefleet updates (see DeviceTelemetryManagementTable.isLikelyTransitioning), so keeping it pretty long to make sure we
 * catch the new state.
 */
const SATELLITE_TAGS_REFRESH_DELAY = 15_000;

const postSubscriptionTagsTransaction = ({
    subscriptionTagChanges = {},
    subscriptionItems,
}: {
    subscriptionTagChanges?: UpdateSubscriptionTagsConfigurationsParams;
    subscriptionItems: UpdateSubscriptionItem[];
}): Promise<UpdatedSubscriptionTagsConfigurationsResponse> => {
    return axios.post(`/api/v2/subscription-tags/telemetry-transaction`, {
        subscription_tag_changes: subscriptionTagChanges,
        subscription_items: subscriptionItems,
    });
};

type UseSubscriptionTagsTransactionOptions = {
    config?: MutationConfig<typeof postSubscriptionTagsTransaction>;
};

export const useSubscriptionTagsTransaction = ({ config }: UseSubscriptionTagsTransactionOptions = {}) => {
    return useMutation({
        ...config,
        mutationFn: postSubscriptionTagsTransaction,
        onSuccess: (_data, { subscriptionTagChanges }) => {
            queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_TAGS_INFO_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ['stripe-subscription-info'] });
            queryClient.invalidateQueries({ queryKey: ['stripe-updated-quantity-upcoming-invoice'] });
            queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ['stripe-invoices-list'] });
            if (some(subscriptionTagChanges, ({ should_iridium_be_active }) => !isNil(should_iridium_be_active))) {
                // we made some satellite changes, it'll take a moment before wavefleet gives us an accurate transitioning network status
                setTimeout(
                    () => queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_TAGS_INFO_QUERY_KEY] }),
                    SATELLITE_TAGS_REFRESH_DELAY
                );
            }
        },
    });
};
